// ─── Adventure & language registry ───
// One place that knows every language Little Sponges teaches and every
// adventure in the curriculum (from Natalya's Drive image library, which has
// one subfolder per adventure). The navigation screen renders straight from
// these lists; the game engine loads a content pack via getAdventurePack.
//
// status values:
//   'ready'    → playable now (tile launches the game)
//   'building' → in production (tile shows an "In the works" badge)
//   'soon'     → planned (tile shows a lock + "Coming soon")
import farm from './farm';
import playground from './playground';
import farmEs from './farm-es';
import school from './school';
import garden from './garden';

// 8 languages on the Little Sponges platform (confirmed from the site
// 2026-05-28). English is the production tutor language; the other 7 are
// Phase 3 scope — shown so the roadmap is visible, locked until built.
export const LANGUAGES = [
  { id: 'en', name: 'English', native: 'English', flag: '🇺🇸', status: 'ready' },
  { id: 'es', name: 'Spanish', native: 'Español', flag: '🇪🇸', status: 'ready' },
  { id: 'zh', name: 'Chinese', native: '中文', flag: '🇨🇳', status: 'soon' },
  { id: 'fr', name: 'French', native: 'Français', flag: '🇫🇷', status: 'soon' },
  { id: 'de', name: 'German', native: 'Deutsch', flag: '🇩🇪', status: 'soon' },
  { id: 'ar', name: 'Arabic', native: 'العربية', flag: '🇸🇦', status: 'soon' },
  { id: 'ru', name: 'Russian', native: 'Русский', flag: '🇷🇺', status: 'soon' },
  { id: 'ja', name: 'Japanese', native: '日本語', flag: '🇯🇵', status: 'soon' },
];

// The 15 curriculum adventures (folder names from the Drive image library).
// Order: Playground comes first in the curriculum, then Farm; the rest are
// display order for the map — adjust freely as Natalya sequences them.
export const ADVENTURES = [
  { id: 'playground', title: 'Playground', emoji: '🛝', status: 'ready' },
  { id: 'farm', title: 'Farm', emoji: '🚜', status: 'ready' },
  { id: 'school', title: 'School', emoji: '🎒', status: 'ready' },
  { id: 'garden', title: 'Garden', emoji: '🌻', status: 'ready' },
  { id: 'numbers-colors', title: 'Numbers & Colors', emoji: '🔢', status: 'soon' },
  { id: 'forest', title: 'Forest', emoji: '🌲', status: 'soon' },
  { id: 'jungle', title: 'Jungle', emoji: '🐒', status: 'soon' },
  { id: 'safari', title: 'Safari', emoji: '🦁', status: 'soon' },
  { id: 'beach', title: 'Beach', emoji: '🏖️', status: 'soon' },
  { id: 'ocean', title: 'Ocean', emoji: '🐠', status: 'soon' },
  { id: 'gym', title: 'Gym', emoji: '⛹️', status: 'soon' },
  { id: 'dentist', title: 'Dentist', emoji: '🦷', status: 'soon' },
  { id: 'music', title: 'Music', emoji: '🎵', status: 'soon' },
  { id: 'concert', title: 'Concert', emoji: '🎤', status: 'soon' },
  { id: 'space', title: 'Space', emoji: '🚀', status: 'soon' },
];

const PACKS = { farm, playground, school, garden };

// Language-variant packs (2026-08-12, Spanish track): keyed '<adventure>:<lang>'.
// Only combinations listed here are playable in that language.
const LANG_PACKS = { 'farm:es': farmEs };

// Which adventures are playable per language. `null` → use each adventure's
// own status (English, the default). For other languages only the listed ids
// unlock; everything else shows "Coming soon".
const READY_BY_LANG = { es: ['farm'] };

// Per-language view of the adventure list for the nav screen.
export function adventuresFor(langId) {
  const ready = READY_BY_LANG[langId];
  if (!ready) return ADVENTURES; // English keeps per-adventure statuses
  return ADVENTURES.map((a) => (ready.includes(a.id) ? a : { ...a, status: 'soon' }));
}

// Resolve the content pack for a playable adventure. Unknown/absent ids fall
// back to Farm so every existing link (WordPress embeds carry no adventure
// param) keeps working exactly as before. A lang id selects the language
// variant when one exists; unknown lang/adventure combos fall back to the
// English pack — never a broken screen.
export function getAdventurePack(id, lang) {
  if (lang && lang !== 'en' && LANG_PACKS[`${id}:${lang}`]) return LANG_PACKS[`${id}:${lang}`];
  return PACKS[id] || farm;
}
